import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Icons } from './icons'

const buttonVariants = cva(
  [
    'group/btn relative inline-flex select-none items-center justify-center',
    'gap-2 whitespace-nowrap',
    'rounded-lg',
    'text-sm font-medium tracking-[-0.005em]',
    'ring-offset-background',
    'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-smooth',
    'active:translate-y-px active:shadow-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: [
          'border border-border-default bg-white text-text-primary',
          'shadow-[0_1px_2px_rgba(16,24,40,0.06)]',
          'hover:border-border-strong hover:bg-background-subtle hover:shadow-[0_2px_6px_rgba(16,24,40,0.08)]',
        ],
        primary: [
          'border border-brand-navy/40 bg-brand-gradient text-white',
          'shadow-[0_1px_2px_rgb(var(--brand-shadow-rgb)/0.35),inset_0_1px_0_rgba(255,255,255,0.14)]',
          'hover:brightness-110 hover:shadow-[0_6px_16px_rgb(var(--accent-rgb)/0.45),inset_0_1px_0_rgba(255,255,255,0.18)]',
        ],
        subtle: [
          'bg-transparent border-0 text-text-secondary',
          'hover:bg-background-subtle hover:text-text-primary',
        ],
        selected: [
          'border border-primary-200 bg-primary-50 text-brand-navy',
          'hover:bg-primary-100',
        ],
        link: [
          'h-auto rounded-sm bg-transparent border-0 p-0 text-text-link shadow-none',
          'hover:text-text-link-hover hover:underline underline-offset-4',
        ],
        outline: [
          'border border-brand-navy/30 bg-white text-brand-navy',
          'hover:border-brand-navy/60 hover:bg-primary-50 hover:shadow-[0_2px_6px_rgb(var(--brand-shadow-rgb)/0.10)]',
        ],
        ghost: [
          'bg-transparent border-0',
          'text-text-secondary hover:bg-background-subtle hover:text-text-primary',
        ],
        glass: [
          'border border-border-default bg-white/80 backdrop-blur-md',
          'text-text-primary shadow-[0_1px_2px_rgba(16,24,40,0.06)] hover:bg-white',
        ],
        gradient: [
          'border border-brand-navy-dark bg-gradient-to-b from-brand-navy-light to-brand-navy text-white',
          'shadow-[0_1px_2px_rgb(var(--brand-shadow-rgb)/0.35),inset_0_1px_0_rgba(255,255,255,0.14)]',
          'hover:from-brand-navy-lighter hover:to-brand-navy-dark hover:shadow-[0_4px_12px_rgb(var(--brand-shadow-rgb)/0.28),inset_0_1px_0_rgba(255,255,255,0.18)]',
        ],
        destructive: [
          'border border-red-700 bg-gradient-to-b from-red-500 to-red-600 text-white',
          'shadow-[0_1px_2px_rgba(185,28,28,0.35),inset_0_1px_0_rgba(255,255,255,0.16)]',
          'hover:from-red-500 hover:to-red-700 hover:shadow-[0_4px_12px_rgba(185,28,28,0.28)]',
        ],
      },
      size: {
        small: 'h-8 px-3 text-xs',
        default: 'h-10 px-4 text-sm',
        large: 'h-11 px-6 text-sm',
        lg: 'h-11 px-6 text-sm',
        'icon-sm': 'h-8 w-8 p-0',
        icon: 'h-10 w-10 p-0',
        'icon-lg': 'h-11 w-11 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && !asChild ? (
          <>
            <Icons.loader className='h-4 w-4 animate-spin' />
            <span>{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
